import { Random } from '@educraft/base';
import {
  PdfFont,
  PdfRect,
  PdfBrush,
  PdfPen,
  PdfColor,
  PdfPoint,
  ETextAlign,
  PageGen,
} from '../pdf';
import '../draw/primitives';
import '../draw/string';

export type SudokuSize = 4 | 9 | 16;

export enum ESudokuLevel {
  Easy = 0,
  Medium = 1,
  Hard = 2,
  Expert = 3,
}

export interface SudokuBoard {
  size: SudokuSize;
  boxSize: number;
  solution: number[][];
  puzzle: number[][];
  clueCount: number;
}

// [size][level] = target number of clues (given cells) in the puzzle
const CLUE_TARGETS: Record<SudokuSize, [number, number, number, number]> = {
  4: [14, 12, 10, 7],
  9: [40, 32, 27, 22],
  16: [160, 130, 110, 96],
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function shuffleArray<T>(arr: T[], random: Random): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = random.next(i + 1);
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
}

function makeGrid(size: number): number[][] {
  return Array.from({ length: size }, () => new Array<number>(size).fill(0));
}

function copyGrid(src: number[][], size: number): number[][] {
  return src.map((row) => row.slice(0, size));
}

// Count the number of set bits in a 32-bit integer
function popcount(n: number): number {
  n = n >>> 0;
  n -= (n >>> 1) & 0x55555555;
  n = (n & 0x33333333) + ((n >>> 2) & 0x33333333);
  n = (n + (n >>> 4)) & 0x0f0f0f0f;
  return Math.imul(n, 0x01010101) >>> 24;
}

// Extract the lowest set bit position (0-indexed); returns -1 if n == 0
function lowestBit(n: number): number {
  if (n === 0) return -1;
  return 31 - Math.clz32(n & -n);
}

// ── Fill grid (randomised backtracking) ───────────────────────────────────────

function fillGrid(
  grid: number[][],
  size: number,
  boxSize: number,
  random: Random,
  rowMask: number[],
  colMask: number[],
  boxMask: number[]
): boolean {
  // Find first empty cell in row-major order
  let fr = -1,
    fc = -1;
  outer: for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (grid[r][c] === 0) {
        fr = r;
        fc = c;
        break outer;
      }
    }
  }
  if (fr === -1) return true; // all filled

  const boxIdx = Math.floor(fr / boxSize) * boxSize + Math.floor(fc / boxSize);
  const used = rowMask[fr] | colMask[fc] | boxMask[boxIdx];
  const fullMask = (1 << size) - 1;
  const avail = ~used & fullMask;

  if (avail === 0) return false;

  // Build shuffled candidate list (values 1..size)
  const candidates: number[] = [];
  let tmp = avail;
  while (tmp !== 0) {
    const bit = tmp & -tmp;
    candidates.push(lowestBit(bit) + 1); // bit position → 1-indexed value
    tmp ^= bit;
  }
  shuffleArray(candidates, random);

  for (const val of candidates) {
    const bit = 1 << (val - 1);
    grid[fr][fc] = val;
    rowMask[fr] |= bit;
    colMask[fc] |= bit;
    boxMask[boxIdx] |= bit;

    if (fillGrid(grid, size, boxSize, random, rowMask, colMask, boxMask)) return true;

    grid[fr][fc] = 0;
    rowMask[fr] ^= bit;
    colMask[fc] ^= bit;
    boxMask[boxIdx] ^= bit;
  }
  return false;
}

// ── Solution counter (MRV heuristic + bitmask, no randomness) ─────────────────

function countSolutions(
  grid: number[][],
  size: number,
  boxSize: number,
  limit: number,
  rowMask: number[],
  colMask: number[],
  boxMask: number[]
): number {
  const fullMask = (1 << size) - 1;

  // Find the empty cell with the fewest candidates (MRV)
  let bestR = -1,
    bestC = -1,
    bestCount = size + 1;
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (grid[r][c] !== 0) continue;
      const boxIdx = Math.floor(r / boxSize) * boxSize + Math.floor(c / boxSize);
      const avail = ~(rowMask[r] | colMask[c] | boxMask[boxIdx]) & fullMask;
      const cnt = popcount(avail);
      if (cnt === 0) return 0; // dead end
      if (cnt < bestCount) {
        bestCount = cnt;
        bestR = r;
        bestC = c;
      }
    }
  }
  if (bestR === -1) return 1; // no empty cell → one solution found

  const boxIdx = Math.floor(bestR / boxSize) * boxSize + Math.floor(bestC / boxSize);
  const avail = ~(rowMask[bestR] | colMask[bestC] | boxMask[boxIdx]) & fullMask;

  let total = 0;
  let tmp = avail;
  while (tmp !== 0) {
    const bit = tmp & -tmp;
    tmp ^= bit;
    const val = lowestBit(bit) + 1;

    grid[bestR][bestC] = val;
    rowMask[bestR] |= bit;
    colMask[bestC] |= bit;
    boxMask[boxIdx] |= bit;

    total += countSolutions(grid, size, boxSize, limit, rowMask, colMask, boxMask);

    grid[bestR][bestC] = 0;
    rowMask[bestR] ^= bit;
    colMask[bestC] ^= bit;
    boxMask[boxIdx] ^= bit;

    if (total >= limit) return total; // early exit
  }
  return total;
}

// ── Build bitmasks from an existing grid ─────────────────────────────────────

function buildMasks(
  grid: number[][],
  size: number,
  boxSize: number
): { rowMask: number[]; colMask: number[]; boxMask: number[] } {
  const rowMask = new Array<number>(size).fill(0);
  const colMask = new Array<number>(size).fill(0);
  const boxCount = size; // size = boxSize * boxSize, so there are size boxes
  const boxMask = new Array<number>(boxCount).fill(0);

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const val = grid[r][c];
      if (val === 0) continue;
      const bit = 1 << (val - 1);
      const boxIdx = Math.floor(r / boxSize) * boxSize + Math.floor(c / boxSize);
      rowMask[r] |= bit;
      colMask[c] |= bit;
      boxMask[boxIdx] |= bit;
    }
  }
  return { rowMask, colMask, boxMask };
}

// ── Remove cells while preserving unique solution ─────────────────────────────

function removeCells(
  solution: number[][],
  size: number,
  boxSize: number,
  clueTarget: number,
  random: Random
): number[][] {
  const puzzle = copyGrid(solution, size);
  const total = size * size;

  // Shuffled list of all cell positions
  const positions = Array.from({ length: total }, (_, i) => i);
  shuffleArray(positions, random);

  let clues = total;

  for (const pos of positions) {
    if (clues <= clueTarget) break;

    const r = Math.floor(pos / size);
    const c = pos % size;
    if (puzzle[r][c] === 0) continue;

    const saved = puzzle[r][c];
    puzzle[r][c] = 0;

    const { rowMask, colMask, boxMask } = buildMasks(puzzle, size, boxSize);
    const solutions = countSolutions(puzzle, size, boxSize, 2, rowMask, colMask, boxMask);

    if (solutions === 1) {
      clues--;
    } else {
      puzzle[r][c] = saved; // restore — removing this cell breaks uniqueness
    }
  }

  return puzzle;
}

// ── Drawing ───────────────────────────────────────────────────────────────────

/** Returns the display label for a cell value (1-9 as digits, 10-16 as A-G for 16×16). */
export function sudokuCellLabel(val: number, size: SudokuSize): string {
  if (size < 16 || val <= 9) return String(val);
  return String.fromCharCode(55 + val); // 10→A, 11→B, …, 16→G
}

/**
 * Draws a single Sudoku grid on the given page.
 *
 * @param page         Target page
 * @param board        The board to render
 * @param originX      Left edge of the grid (pdf units)
 * @param originY      Top edge of the grid (pdf units)
 * @param cellPt       Side length of one cell (pdf units)
 * @param givenFont    Font used for given (printed) clues
 * @param solutionFont Font used for solution numbers when showSolution=true
 * @param showSolution If true, all cells (including empty ones) show their solution value
 */
export function drawSudokuGrid(
  page: PageGen,
  board: SudokuBoard,
  originX: number,
  originY: number,
  cellPt: number,
  givenFont: PdfFont,
  solutionFont: PdfFont,
  showSolution: boolean
): void {
  const { size, boxSize, puzzle, solution } = board;
  const total = size * cellPt;

  const outerPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), cellPt * 0.06);
  const boxPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), cellPt * 0.035);
  const cellPen = new PdfPen(PdfColor.fromRgb(170, 170, 170), cellPt * 0.013);
  const givenBrush = PdfBrush.Black;
  const solutionBrush = new PdfBrush(PdfColor.fromRgb(60, 100, 200));
  const givenBg = new PdfBrush(PdfColor.fromRgb(232, 232, 232));

  // Cell backgrounds and numbers
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const cx = originX + c * cellPt;
      const cy = originY + r * cellPt;
      const cellRect = new PdfRect(cx, cy, cellPt, cellPt);
      const given = puzzle[r][c];

      if (given !== 0) {
        page.drawRectangle(null, givenBg, cellRect);
        page.drawString(
          sudokuCellLabel(given, board.size),
          givenFont,
          givenBrush,
          null,
          cellRect,
          ETextAlign.Center
        );
      } else if (showSolution) {
        page.drawString(
          sudokuCellLabel(solution[r][c], board.size),
          solutionFont,
          solutionBrush,
          null,
          cellRect,
          ETextAlign.Center
        );
      }
    }
  }

  // Thin cell-border lines (skip box boundaries)
  for (let i = 1; i < size; i++) {
    if (i % boxSize === 0) continue;
    const pos = i * cellPt;
    page.drawLine(
      cellPen,
      new PdfPoint(originX, originY + pos),
      new PdfPoint(originX + total, originY + pos)
    );
    page.drawLine(
      cellPen,
      new PdfPoint(originX + pos, originY),
      new PdfPoint(originX + pos, originY + total)
    );
  }

  // Medium box-border lines
  for (let i = boxSize; i < size; i += boxSize) {
    const pos = i * cellPt;
    page.drawLine(
      boxPen,
      new PdfPoint(originX, originY + pos),
      new PdfPoint(originX + total, originY + pos)
    );
    page.drawLine(
      boxPen,
      new PdfPoint(originX + pos, originY),
      new PdfPoint(originX + pos, originY + total)
    );
  }

  // Thick outer border
  page.drawRectangle(outerPen, null, new PdfRect(originX, originY, total, total));
}

// ── Public entry point ───────────────────────────────────────────────────────

export function generateSudoku(random: Random, size: SudokuSize, level: ESudokuLevel): SudokuBoard {
  const boxSize = Math.round(Math.sqrt(size)) as 2 | 3 | 4;
  const clueTarget = CLUE_TARGETS[size][level];

  // 1. Generate a fully solved grid
  const solution = makeGrid(size);
  const rowMask = new Array<number>(size).fill(0);
  const colMask = new Array<number>(size).fill(0);
  const boxMask = new Array<number>(size).fill(0);
  fillGrid(solution, size, boxSize, random, rowMask, colMask, boxMask);

  // 2. Remove cells to create the puzzle
  const puzzle = removeCells(solution, size, boxSize, clueTarget, random);

  const clueCount = puzzle.reduce((sum, row) => sum + row.filter((v) => v !== 0).length, 0);

  return { size, boxSize, solution, puzzle, clueCount };
}
